import type { ReactNode } from 'react';
import { Building2, ChevronRight, MapPin, Users, type LucideIcon } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatLocation } from '@/utils/formatLocation';
import { getInitialsFromName } from '@/utils/getInitialsFromName';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import {
  TEACHER_CARD_CLASS,
  TEACHER_STATUS_DOT_CLASS,
  TEACHER_STATUS_LABELS,
} from '../constants/teacherSubmission.constants';
import type { TeacherDetail } from '../types/teacherSubmission.types';
import { TeacherCategoryBadge } from './TeacherCategoryBadge';
import { TeacherInfoRow, TeacherInfoRowsSkeleton } from './TeacherInfoCard';
import {
  TeacherProfileCompleteness,
  TeacherProfileCompletenessSkeleton,
} from './TeacherProfileCompleteness';

const CONTACT_ROW_COUNT = 2;

interface IconLineProps {
  icon: LucideIcon;
  children: ReactNode;
}

function IconLine({ icon: Icon, children }: IconLineProps) {
  return (
    <p className="flex items-start gap-2">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

interface TeacherProfileCardProps {
  teacher: TeacherDetail;
  onShowOnlyThisInstitute: () => void;
  className?: string | undefined;
}

export function TeacherProfileCard({
  teacher,
  onShowOnlyThisInstitute,
  className,
}: TeacherProfileCardProps) {
  const location = formatLocation(teacher);

  return (
    <section className={cn(TEACHER_CARD_CLASS, className)}>
      <div className="flex flex-col items-center gap-3 p-5 text-center">
        <Avatar className="size-20 ring-4 ring-primary/10">
          {teacher.photoUrl && <AvatarImage src={teacher.photoUrl} alt={teacher.name} />}
          <AvatarFallback className={cn('text-xl font-semibold', ACCENT_MARK_CLASS)}>
            {getInitialsFromName(teacher.name)}
          </AvatarFallback>
          <span
            role="img"
            aria-label={TEACHER_STATUS_LABELS[teacher.status]}
            title={TEACHER_STATUS_LABELS[teacher.status]}
            className={cn(
              'absolute right-1.5 bottom-1.5 z-10 size-3 rounded-full ring-2 ring-card',
              TEACHER_STATUS_DOT_CLASS[teacher.status],
            )}
          />
        </Avatar>

        <div className="flex flex-col gap-1">
          <h2 className="text-lg leading-tight font-semibold text-balance">{teacher.name}</h2>
          {teacher.designation && (
            <p className="text-sm text-muted-foreground">{teacher.designation.name}</p>
          )}
        </div>

        {teacher.category && <TeacherCategoryBadge category={teacher.category} />}
      </div>

      <div className="flex flex-col gap-2 border-t px-4 py-3 text-sm">
        <IconLine icon={Building2}>{teacher.institute.name}</IconLine>
        {location && <IconLine icon={MapPin}>{location}</IconLine>}
        <Button
          size="sm"
          className="mt-1 w-full justify-between font-semibold max-lg:h-10"
          onClick={onShowOnlyThisInstitute}
        >
          <span className="flex items-center gap-2">
            <Users aria-hidden />
            See all teachers of this institute
          </span>
          <ChevronRight aria-hidden />
        </Button>
      </div>

      <div className="border-t px-4 py-1">
        <dl>
          <TeacherInfoRow label="Mobile">{teacher.mobile}</TeacherInfoRow>
          <TeacherInfoRow label="Other mobile">{teacher.secondaryMobile}</TeacherInfoRow>
        </dl>
      </div>

      <div className="border-t p-4">
        <TeacherProfileCompleteness teacher={teacher} />
      </div>
    </section>
  );
}

export function TeacherProfileCardSkeleton() {
  return (
    <div className={TEACHER_CARD_CLASS} aria-hidden>
      <div className="flex flex-col items-center gap-3 p-5">
        <Skeleton className="size-20 rounded-full" />
        <div className="flex flex-col items-center gap-2">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-28" />
        </div>
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>

      <div className="flex flex-col gap-3 border-t px-4 py-3.5">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-24" />
      </div>

      <div className="border-t px-4 py-1">
        <TeacherInfoRowsSkeleton rowCount={CONTACT_ROW_COUNT} />
      </div>

      <div className="border-t p-4">
        <TeacherProfileCompletenessSkeleton />
      </div>
    </div>
  );
}
